/**
 * Курируемый реестр иконок пространств — большой набор Lucide с упором на
 * технику и разработку, сгруппированный по доменам. Поиск в пикере.
 *
 * Соглашение для поля `space.icon` (строка, без изменения схемы БД):
 *  - `'i:<key>'`, где `<key>` есть в реестре → Lucide-иконка;
 *  - пусто/иное → буквенная монограмма (см. SpaceAvatar).
 */

import type { LucideIcon } from 'lucide-react';
import {
  // dev
  Code, Code2, CodeXml, Terminal, SquareTerminal, Braces, Brackets, Binary, Bug, BugOff,
  Regex, Variable, Hash, FileCode, FileCode2, FileJson, FileTerminal, FileDiff, Parentheses, Command, Keyboard,
  // git
  GitBranch, GitMerge, GitPullRequest, GitFork, GitCompare, GitGraph, GitCommitHorizontal,
  History, RefreshCw, RotateCw, Repeat, Undo2,
  // ci/cd build
  Hammer, Wrench, Cog, Settings, Settings2, Package, PackageOpen, Box, Boxes, Container,
  Workflow, Webhook, Bolt, Zap, Play, Power,
  // infra
  Server, ServerCog, ServerCrash, Database, DatabaseZap, DatabaseBackup, HardDrive, MemoryStick,
  Cpu, CircuitBoard, Cloud, CloudCog, CloudUpload, CloudDownload, Layers, Network, Router,
  // network
  Wifi, WifiOff, Cable, Usb, Bluetooth, Radio, Antenna, Satellite, SatelliteDish, Radar, Rss,
  Globe, Share2, Waypoints, Spline, Split, Merge, Combine,
  // devices
  Monitor, MonitorSmartphone, Laptop, Smartphone, Tablet, Computer, AppWindow, PanelTop,
  LayoutTemplate, MousePointer, MousePointer2, ScanLine, QrCode, Barcode, Fingerprint,
  // security
  Shield, ShieldCheck, ShieldAlert, ShieldHalf, Lock, LockKeyhole, Unlock, Key, KeyRound, KeySquare, Eye, EyeOff,
  // testing & quality
  TestTube, TestTubeDiagonal, FlaskConical, Beaker, Microscope, Atom, Gauge, Activity, ActivitySquare,
  // ai & data
  Brain, BrainCircuit, Bot, BotMessageSquare, Sparkles, Dna,
  // architecture
  Component, Blocks, Puzzle, Grid2x2, LayoutGrid, LayoutDashboard, Table, Kanban,
  // goals
  Target, Goal, Rocket, Flag, Trophy, Award, Crown, Star, Lightbulb, Gem, TrendingUp, TrendingDown,
  // analytics
  ChartBar, ChartLine, ChartPie,
  // finance
  Wallet, DollarSign, CreditCard, Banknote, Coins, PiggyBank, Landmark, Receipt,
  // comms
  MessageSquare, MessageCircle, Mail, Bell, Megaphone, Send, Phone,
  // docs
  FileText, Files, Folder, FolderOpen, FolderGit2, Book, BookOpen, Library, Bookmark, Clipboard, ClipboardList,
  // time
  Calendar, CalendarClock, CalendarDays, Clock, Timer, Hourglass,
  // people
  Users, User, UserCircle, Contact, Briefcase, Building, Building2, Handshake,
  // places & transport
  MapPin, Map as MapIcon, Compass, Navigation, Home, Store, Warehouse, Factory, Truck, Car, Plane, Ship, Train,
  // misc
  Leaf, Sprout, TreePine, Sun, Moon, Droplet, Flame, Heart, Gift, ShoppingCart, Anchor, Diamond, Tags, Tag,
} from 'lucide-react';

export interface SpaceIconDef {
  key: string;
  label: string;
  group: string;
  Icon: LucideIcon;
}

const G = {
  dev: 'Разработка',
  git: 'Версионирование',
  ci: 'CI/CD и сборка',
  infra: 'Инфраструктура',
  net: 'Сеть',
  dev_hw: 'Устройства',
  sec: 'Безопасность',
  qa: 'Тестирование',
  ai: 'AI и данные',
  arch: 'Архитектура',
  goals: 'Цели и рост',
  analytics: 'Аналитика',
  fin: 'Финансы',
  comms: 'Коммуникации',
  docs: 'Документы',
  time: 'Время и план',
  people: 'Команды и люди',
  geo: 'Места и транспорт',
  misc: 'Прочее',
};

export const SPACE_ICONS: SpaceIconDef[] = [
  // ── Разработка ──
  { key: 'code', label: 'Код', group: G.dev, Icon: Code },
  { key: 'code2', label: 'Код', group: G.dev, Icon: Code2 },
  { key: 'code-xml', label: 'XML/HTML', group: G.dev, Icon: CodeXml },
  { key: 'terminal', label: 'Терминал', group: G.dev, Icon: Terminal },
  { key: 'terminal-square', label: 'Консоль', group: G.dev, Icon: SquareTerminal },
  { key: 'braces', label: 'JSON', group: G.dev, Icon: Braces },
  { key: 'brackets', label: 'Массив', group: G.dev, Icon: Brackets },
  { key: 'parentheses', label: 'Функция', group: G.dev, Icon: Parentheses },
  { key: 'binary', label: 'Бинарь', group: G.dev, Icon: Binary },
  { key: 'regex', label: 'Regex', group: G.dev, Icon: Regex },
  { key: 'variable', label: 'Переменная', group: G.dev, Icon: Variable },
  { key: 'hash', label: 'Хэш', group: G.dev, Icon: Hash },
  { key: 'bug', label: 'Баг', group: G.dev, Icon: Bug },
  { key: 'bug-off', label: 'Фикс', group: G.dev, Icon: BugOff },
  { key: 'file-code', label: 'Файл кода', group: G.dev, Icon: FileCode },
  { key: 'file-code2', label: 'Исходник', group: G.dev, Icon: FileCode2 },
  { key: 'file-json', label: 'JSON-файл', group: G.dev, Icon: FileJson },
  { key: 'file-terminal', label: 'Скрипт', group: G.dev, Icon: FileTerminal },
  { key: 'file-diff', label: 'Дифф', group: G.dev, Icon: FileDiff },
  { key: 'command', label: 'Команда', group: G.dev, Icon: Command },
  { key: 'keyboard', label: 'Клавиатура', group: G.dev, Icon: Keyboard },

  // ── Версионирование ──
  { key: 'git-branch', label: 'Ветка', group: G.git, Icon: GitBranch },
  { key: 'git-merge', label: 'Мердж', group: G.git, Icon: GitMerge },
  { key: 'git-pull-request', label: 'PR', group: G.git, Icon: GitPullRequest },
  { key: 'git-fork', label: 'Форк', group: G.git, Icon: GitFork },
  { key: 'git-compare', label: 'Сравнение', group: G.git, Icon: GitCompare },
  { key: 'git-graph', label: 'Граф коммитов', group: G.git, Icon: GitGraph },
  { key: 'git-commit', label: 'Коммит', group: G.git, Icon: GitCommitHorizontal },
  { key: 'history', label: 'История', group: G.git, Icon: History },
  { key: 'refresh', label: 'Синхронизация', group: G.git, Icon: RefreshCw },
  { key: 'rotate', label: 'Откат', group: G.git, Icon: RotateCw },
  { key: 'repeat', label: 'Повтор', group: G.git, Icon: Repeat },
  { key: 'undo', label: 'Отмена', group: G.git, Icon: Undo2 },

  // ── CI/CD и сборка ──
  { key: 'hammer', label: 'Сборка', group: G.ci, Icon: Hammer },
  { key: 'wrench', label: 'Поддержка', group: G.ci, Icon: Wrench },
  { key: 'cog', label: 'Процесс', group: G.ci, Icon: Cog },
  { key: 'settings', label: 'Настройки', group: G.ci, Icon: Settings },
  { key: 'settings2', label: 'Конфиг', group: G.ci, Icon: Settings2 },
  { key: 'package', label: 'Пакет', group: G.ci, Icon: Package },
  { key: 'package-open', label: 'Релиз', group: G.ci, Icon: PackageOpen },
  { key: 'box', label: 'Артефакт', group: G.ci, Icon: Box },
  { key: 'boxes', label: 'Модули', group: G.ci, Icon: Boxes },
  { key: 'container', label: 'Контейнер', group: G.ci, Icon: Container },
  { key: 'workflow', label: 'Пайплайн', group: G.ci, Icon: Workflow },
  { key: 'webhook', label: 'Вебхук', group: G.ci, Icon: Webhook },
  { key: 'bolt', label: 'Билд', group: G.ci, Icon: Bolt },
  { key: 'zap', label: 'Быстро', group: G.ci, Icon: Zap },
  { key: 'play', label: 'Запуск', group: G.ci, Icon: Play },
  { key: 'power', label: 'Деплой', group: G.ci, Icon: Power },

  // ── Инфраструктура ──
  { key: 'server', label: 'Сервер', group: G.infra, Icon: Server },
  { key: 'server-cog', label: 'Сервер+', group: G.infra, Icon: ServerCog },
  { key: 'server-crash', label: 'Инцидент', group: G.infra, Icon: ServerCrash },
  { key: 'database', label: 'БД', group: G.infra, Icon: Database },
  { key: 'database-zap', label: 'Кэш', group: G.infra, Icon: DatabaseZap },
  { key: 'database-backup', label: 'Бэкап', group: G.infra, Icon: DatabaseBackup },
  { key: 'hard-drive', label: 'Диск', group: G.infra, Icon: HardDrive },
  { key: 'memory', label: 'Память', group: G.infra, Icon: MemoryStick },
  { key: 'cpu', label: 'CPU', group: G.infra, Icon: Cpu },
  { key: 'circuit-board', label: 'Плата', group: G.infra, Icon: CircuitBoard },
  { key: 'cloud', label: 'Облако', group: G.infra, Icon: Cloud },
  { key: 'cloud-cog', label: 'Облако+', group: G.infra, Icon: CloudCog },
  { key: 'cloud-upload', label: 'Выгрузка', group: G.infra, Icon: CloudUpload },
  { key: 'cloud-download', label: 'Загрузка', group: G.infra, Icon: CloudDownload },
  { key: 'layers', label: 'Слои', group: G.infra, Icon: Layers },
  { key: 'network', label: 'Сеть', group: G.infra, Icon: Network },
  { key: 'router', label: 'Роутер', group: G.infra, Icon: Router },

  // ── Сеть ──
  { key: 'wifi', label: 'Wi-Fi', group: G.net, Icon: Wifi },
  { key: 'wifi-off', label: 'Оффлайн', group: G.net, Icon: WifiOff },
  { key: 'cable', label: 'Кабель', group: G.net, Icon: Cable },
  { key: 'usb', label: 'USB', group: G.net, Icon: Usb },
  { key: 'bluetooth', label: 'Bluetooth', group: G.net, Icon: Bluetooth },
  { key: 'radio', label: 'Радио', group: G.net, Icon: Radio },
  { key: 'antenna', label: 'Антенна', group: G.net, Icon: Antenna },
  { key: 'satellite', label: 'Спутник', group: G.net, Icon: Satellite },
  { key: 'satellite-dish', label: 'Тарелка', group: G.net, Icon: SatelliteDish },
  { key: 'radar', label: 'Радар', group: G.net, Icon: Radar },
  { key: 'rss', label: 'Поток', group: G.net, Icon: Rss },
  { key: 'globe', label: 'Веб', group: G.net, Icon: Globe },
  { key: 'share', label: 'Связи', group: G.net, Icon: Share2 },
  { key: 'waypoints', label: 'Узлы', group: G.net, Icon: Waypoints },
  { key: 'spline', label: 'Граф', group: G.net, Icon: Spline },
  { key: 'split', label: 'Разветвление', group: G.net, Icon: Split },
  { key: 'merge', label: 'Слияние', group: G.net, Icon: Merge },
  { key: 'combine', label: 'Объединение', group: G.net, Icon: Combine },

  // ── Устройства ──
  { key: 'monitor', label: 'Монитор', group: G.dev_hw, Icon: Monitor },
  { key: 'monitor-smartphone', label: 'Адаптив', group: G.dev_hw, Icon: MonitorSmartphone },
  { key: 'laptop', label: 'Ноутбук', group: G.dev_hw, Icon: Laptop },
  { key: 'smartphone', label: 'Смартфон', group: G.dev_hw, Icon: Smartphone },
  { key: 'tablet', label: 'Планшет', group: G.dev_hw, Icon: Tablet },
  { key: 'computer', label: 'ПК', group: G.dev_hw, Icon: Computer },
  { key: 'app-window', label: 'Окно', group: G.dev_hw, Icon: AppWindow },
  { key: 'panel-top', label: 'Панель', group: G.dev_hw, Icon: PanelTop },
  { key: 'layout-template', label: 'Шаблон', group: G.dev_hw, Icon: LayoutTemplate },
  { key: 'mouse-pointer', label: 'Курсор', group: G.dev_hw, Icon: MousePointer },
  { key: 'mouse-pointer2', label: 'Клик', group: G.dev_hw, Icon: MousePointer2 },
  { key: 'scan-line', label: 'Скан', group: G.dev_hw, Icon: ScanLine },
  { key: 'qr-code', label: 'QR', group: G.dev_hw, Icon: QrCode },
  { key: 'barcode', label: 'Штрихкод', group: G.dev_hw, Icon: Barcode },
  { key: 'fingerprint', label: 'Отпечаток', group: G.dev_hw, Icon: Fingerprint },

  // ── Безопасность ──
  { key: 'shield', label: 'Защита', group: G.sec, Icon: Shield },
  { key: 'shield-check', label: 'Аудит', group: G.sec, Icon: ShieldCheck },
  { key: 'shield-alert', label: 'Угроза', group: G.sec, Icon: ShieldAlert },
  { key: 'shield-half', label: 'Политика', group: G.sec, Icon: ShieldHalf },
  { key: 'lock', label: 'Замок', group: G.sec, Icon: Lock },
  { key: 'lock-keyhole', label: 'Шифрование', group: G.sec, Icon: LockKeyhole },
  { key: 'unlock', label: 'Доступ', group: G.sec, Icon: Unlock },
  { key: 'key', label: 'Ключ', group: G.sec, Icon: Key },
  { key: 'key-round', label: 'Токен', group: G.sec, Icon: KeyRound },
  { key: 'key-square', label: 'Секрет', group: G.sec, Icon: KeySquare },
  { key: 'eye', label: 'Просмотр', group: G.sec, Icon: Eye },
  { key: 'eye-off', label: 'Скрыто', group: G.sec, Icon: EyeOff },

  // ── Тестирование ──
  { key: 'test-tube', label: 'Тест', group: G.qa, Icon: TestTube },
  { key: 'test-tube-d', label: 'Пробирка', group: G.qa, Icon: TestTubeDiagonal },
  { key: 'flask', label: 'Эксперимент', group: G.qa, Icon: FlaskConical },
  { key: 'beaker', label: 'Лаба', group: G.qa, Icon: Beaker },
  { key: 'microscope', label: 'Анализ', group: G.qa, Icon: Microscope },
  { key: 'atom', label: 'Атом', group: G.qa, Icon: Atom },
  { key: 'gauge', label: 'Метрика', group: G.qa, Icon: Gauge },
  { key: 'activity', label: 'Мониторинг', group: G.qa, Icon: Activity },
  { key: 'activity-square', label: 'Здоровье', group: G.qa, Icon: ActivitySquare },

  // ── AI и данные ──
  { key: 'brain', label: 'AI', group: G.ai, Icon: Brain },
  { key: 'brain-circuit', label: 'ML', group: G.ai, Icon: BrainCircuit },
  { key: 'bot', label: 'Бот', group: G.ai, Icon: Bot },
  { key: 'bot-message', label: 'Ассистент', group: G.ai, Icon: BotMessageSquare },
  { key: 'sparkles', label: 'Генерация', group: G.ai, Icon: Sparkles },
  { key: 'dna', label: 'Модель', group: G.ai, Icon: Dna },

  // ── Архитектура ──
  { key: 'component', label: 'Компонент', group: G.arch, Icon: Component },
  { key: 'blocks', label: 'Блоки', group: G.arch, Icon: Blocks },
  { key: 'puzzle', label: 'Интеграция', group: G.arch, Icon: Puzzle },
  { key: 'grid2x2', label: 'Матрица', group: G.arch, Icon: Grid2x2 },
  { key: 'layout-grid', label: 'Сетка', group: G.arch, Icon: LayoutGrid },
  { key: 'dashboard', label: 'Дашборд', group: G.arch, Icon: LayoutDashboard },
  { key: 'table', label: 'Таблица', group: G.arch, Icon: Table },
  { key: 'kanban', label: 'Канбан', group: G.arch, Icon: Kanban },

  // ── Цели и рост ──
  { key: 'target', label: 'Цель', group: G.goals, Icon: Target },
  { key: 'goal', label: 'Гол', group: G.goals, Icon: Goal },
  { key: 'rocket', label: 'Запуск', group: G.goals, Icon: Rocket },
  { key: 'flag', label: 'Веха', group: G.goals, Icon: Flag },
  { key: 'trophy', label: 'Победа', group: G.goals, Icon: Trophy },
  { key: 'award', label: 'Награда', group: G.goals, Icon: Award },
  { key: 'crown', label: 'Лидер', group: G.goals, Icon: Crown },
  { key: 'star', label: 'Избранное', group: G.goals, Icon: Star },
  { key: 'lightbulb', label: 'Идея', group: G.goals, Icon: Lightbulb },
  { key: 'gem', label: 'Ценность', group: G.goals, Icon: Gem },
  { key: 'trending-up', label: 'Рост', group: G.goals, Icon: TrendingUp },
  { key: 'trending-down', label: 'Спад', group: G.goals, Icon: TrendingDown },

  // ── Аналитика ──
  { key: 'chart-bar', label: 'Бары', group: G.analytics, Icon: ChartBar },
  { key: 'chart-line', label: 'Линия', group: G.analytics, Icon: ChartLine },
  { key: 'chart-pie', label: 'Доли', group: G.analytics, Icon: ChartPie },

  // ── Финансы ──
  { key: 'wallet', label: 'Кошелёк', group: G.fin, Icon: Wallet },
  { key: 'dollar', label: 'Деньги', group: G.fin, Icon: DollarSign },
  { key: 'credit-card', label: 'Карта', group: G.fin, Icon: CreditCard },
  { key: 'banknote', label: 'Купюра', group: G.fin, Icon: Banknote },
  { key: 'coins', label: 'Монеты', group: G.fin, Icon: Coins },
  { key: 'piggy-bank', label: 'Копилка', group: G.fin, Icon: PiggyBank },
  { key: 'landmark', label: 'Банк', group: G.fin, Icon: Landmark },
  { key: 'receipt', label: 'Чек', group: G.fin, Icon: Receipt },

  // ── Коммуникации ──
  { key: 'message-square', label: 'Чат', group: G.comms, Icon: MessageSquare },
  { key: 'message-circle', label: 'Сообщения', group: G.comms, Icon: MessageCircle },
  { key: 'mail', label: 'Почта', group: G.comms, Icon: Mail },
  { key: 'bell', label: 'Уведомления', group: G.comms, Icon: Bell },
  { key: 'megaphone', label: 'Анонс', group: G.comms, Icon: Megaphone },
  { key: 'send', label: 'Отправка', group: G.comms, Icon: Send },
  { key: 'phone', label: 'Звонки', group: G.comms, Icon: Phone },

  // ── Документы ──
  { key: 'file-text', label: 'Документ', group: G.docs, Icon: FileText },
  { key: 'files', label: 'Файлы', group: G.docs, Icon: Files },
  { key: 'folder', label: 'Папка', group: G.docs, Icon: Folder },
  { key: 'folder-open', label: 'Открыто', group: G.docs, Icon: FolderOpen },
  { key: 'folder-git', label: 'Репозиторий', group: G.docs, Icon: FolderGit2 },
  { key: 'book', label: 'Книга', group: G.docs, Icon: Book },
  { key: 'book-open', label: 'База знаний', group: G.docs, Icon: BookOpen },
  { key: 'library', label: 'Библиотека', group: G.docs, Icon: Library },
  { key: 'bookmark', label: 'Закладка', group: G.docs, Icon: Bookmark },
  { key: 'clipboard', label: 'Доска', group: G.docs, Icon: Clipboard },
  { key: 'clipboard-list', label: 'Список', group: G.docs, Icon: ClipboardList },

  // ── Время и план ──
  { key: 'calendar', label: 'Календарь', group: G.time, Icon: Calendar },
  { key: 'calendar-clock', label: 'Срок', group: G.time, Icon: CalendarClock },
  { key: 'calendar-days', label: 'Дни', group: G.time, Icon: CalendarDays },
  { key: 'clock', label: 'Время', group: G.time, Icon: Clock },
  { key: 'timer', label: 'Таймер', group: G.time, Icon: Timer },
  { key: 'hourglass', label: 'Дедлайн', group: G.time, Icon: Hourglass },

  // ── Команды и люди ──
  { key: 'users', label: 'Команда', group: G.people, Icon: Users },
  { key: 'user', label: 'Человек', group: G.people, Icon: User },
  { key: 'user-circle', label: 'Профиль', group: G.people, Icon: UserCircle },
  { key: 'contact', label: 'Контакт', group: G.people, Icon: Contact },
  { key: 'briefcase', label: 'Бизнес', group: G.people, Icon: Briefcase },
  { key: 'building', label: 'Отдел', group: G.people, Icon: Building },
  { key: 'building2', label: 'Компания', group: G.people, Icon: Building2 },
  { key: 'handshake', label: 'Сделка', group: G.people, Icon: Handshake },

  // ── Места и транспорт ──
  { key: 'map-pin', label: 'Точка', group: G.geo, Icon: MapPin },
  { key: 'map', label: 'Карта', group: G.geo, Icon: MapIcon },
  { key: 'compass', label: 'Компас', group: G.geo, Icon: Compass },
  { key: 'navigation', label: 'Маршрут', group: G.geo, Icon: Navigation },
  { key: 'home', label: 'Дом', group: G.geo, Icon: Home },
  { key: 'store', label: 'Магазин', group: G.geo, Icon: Store },
  { key: 'warehouse', label: 'Склад', group: G.geo, Icon: Warehouse },
  { key: 'factory', label: 'Завод', group: G.geo, Icon: Factory },
  { key: 'truck', label: 'Доставка', group: G.geo, Icon: Truck },
  { key: 'car', label: 'Авто', group: G.geo, Icon: Car },
  { key: 'plane', label: 'Самолёт', group: G.geo, Icon: Plane },
  { key: 'ship', label: 'Корабль', group: G.geo, Icon: Ship },
  { key: 'train', label: 'Поезд', group: G.geo, Icon: Train },

  // ── Прочее ──
  { key: 'leaf', label: 'Эко', group: G.misc, Icon: Leaf },
  { key: 'sprout', label: 'Старт', group: G.misc, Icon: Sprout },
  { key: 'tree', label: 'Дерево', group: G.misc, Icon: TreePine },
  { key: 'sun', label: 'Солнце', group: G.misc, Icon: Sun },
  { key: 'moon', label: 'Ночь', group: G.misc, Icon: Moon },
  { key: 'droplet', label: 'Капля', group: G.misc, Icon: Droplet },
  { key: 'flame', label: 'Огонь', group: G.misc, Icon: Flame },
  { key: 'heart', label: 'Здоровье', group: G.misc, Icon: Heart },
  { key: 'gift', label: 'Подарок', group: G.misc, Icon: Gift },
  { key: 'cart', label: 'Покупки', group: G.misc, Icon: ShoppingCart },
  { key: 'anchor', label: 'Якорь', group: G.misc, Icon: Anchor },
  { key: 'diamond', label: 'Ромб', group: G.misc, Icon: Diamond },
  { key: 'tags', label: 'Метки', group: G.misc, Icon: Tags },
  { key: 'tag', label: 'Метка', group: G.misc, Icon: Tag },
];

const ICON_BY_KEY = new Map(SPACE_ICONS.map((def) => [def.key, def]));

export const SPACE_ICON_PREFIX = 'i:';

/**
 * Кодировка поля `space.icon` (одна строка, без изменения схемы):
 *  - `i:<key>`            — иконка из реестра, цвет детерминирован из id/имени;
 *  - `i:<key>@RRGGBB`     — иконка + выбранный цвет плитки;
 *  - `@RRGGBB`            — без иконки (монограмма) + выбранный цвет;
 *  - иное/пусто           — монограмма, цвет детерминирован.
 * `@RRGGBB` — 6 hex без `#`.
 */
function splitIconValue(icon?: string | null): { base: string; color: string | null } {
  if (!icon) return { base: '', color: null };
  const at = icon.lastIndexOf('@');
  if (at >= 0) {
    const c = icon.slice(at + 1);
    if (/^[0-9a-fA-F]{6}$/.test(c)) return { base: icon.slice(0, at), color: `#${c}` };
  }
  return { base: icon, color: null };
}

export function resolveSpaceIconKey(icon?: string | null): string | null {
  const { base } = splitIconValue(icon);
  if (!base.startsWith(SPACE_ICON_PREFIX)) return null;
  const key = base.slice(SPACE_ICON_PREFIX.length);
  return ICON_BY_KEY.has(key) ? key : null;
}

/** Явно выбранный цвет плитки (`@RRGGBB`) или null, если цвет не задан. */
export function resolveSpaceColor(icon?: string | null): string | null {
  return splitIconValue(icon).color;
}

/**
 * Собирает значение поля `icon` из ключа иконки (или null) и цвета (или null).
 * Используется пикером: выбор иконки/цвета сохраняет оба в одной строке.
 */
export function buildSpaceIcon(key: string | null, color: string | null): string | null {
  const base = key ? `${SPACE_ICON_PREFIX}${key}` : '';
  const suffix = color ? `@${color.replace('#', '')}` : '';
  return base + suffix || null;
}

export function getSpaceIconDef(key: string): SpaceIconDef | undefined {
  return ICON_BY_KEY.get(key);
}
